<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/stores/userStore";
import { faFileLines, faFilePlus, faUserGroup, faChartMixed, faBookBlank, faGear, faBuildings, faListCheck, faAddressCard, faFileInvoiceDollar, faFilePen, faBoxIsometric } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import LinkWithSubmenu from "@/components/LinkWithSubmenu.vue";
import StatusBadge from "@comp-lib/components/StatusBadge.vue";
import { currentPlanIsNot } from "@/utils";
import { currentDefaultVersion } from "@/assets/mobiusVersioningData";
import type { NavLink, NavMenuSection } from "@/types";

const props = defineProps<{
  isSidebarOpen: boolean;
}>();

const route = useRoute();
const userStore = useUserStore();

const showAdminMenu = computed(() => route.path.startsWith("/admin"));

const userNavSections = computed<NavMenuSection[]>(() => [
  {
    links: [
      {
        routerName: "UserDashboard",
        displayName: "Dashboard",
        icon: faChartMixed
      },
    ]
  },
  {
    category: "Syncing",
    links: [
      {
        displayName: "Run new batch",
        icon: faFilePlus,
        children: [
          { routerName: "iframe-user-create_invoice_batch", displayName: "Invoice", routerParams: { tenant_id: String(userStore.currentTenant?.id) } },
          { routerName: "iframe-user-create_expense_batch", displayName: "Expense", routerParams: { tenant_id: String(userStore.currentTenant?.id) } },
          { routerName: "iframe-user-create_inventory_batch", displayName: "Inventory", routerParams: { tenant_id: String(userStore.currentTenant?.id) } },
          { routerName: "iframe-user-create_sync_all_batch", displayName: "SyncAll", routerParams: { tenant_id: String(userStore.currentTenant?.id) } },
          { routerName: "NewPaymentSync", displayName: "Payment sync" }
        ]
      },
      {
        routerName: "iframe-user-batch_status",
        routerParams: { tenant_id: String(userStore.currentTenant?.id) },
        displayName: "Review existing batch",
        icon: faFileLines
      },
    ]
  },
  {
    category: "Accounting",
    dividerAfter: true,
    links: [
      {
        routerName: "ManageCustomers",
        displayName: "Customers",
        icon: faUserGroup,
        beta: true
      },
      {
        routerName: "ManageInvoices",
        displayName: "Invoices",
        icon: faFileInvoiceDollar,
        beta: true
      },
      {
        routerName: "ManageContracts",
        displayName: "Contracts",
        icon: faFilePen,
        beta: true
      },
      {
        routerName: "ManageProducts",
        displayName: "Products & Services",
        icon: faBoxIsometric,
        beta: true
      },
    ]
  },
  {
    links: [
      {
        displayName: "Resources",
        icon: faBookBlank,
        children: [
          { externalUrl: "https://support.gozynta.com/portal/kb", displayName: "Documentation" },
          { externalUrl: "https://support.gozynta.com/", displayName: "Self-service portal" },
          {
            externalUrl: `${
              window.location.hostname === "localhost"
                ? "https://mobius-staging.gozynta.com"
                : window.location.origin
            }/api/cwacc/${currentDefaultVersion}/user/canny/tenant_id/${userStore.currentTenant?.id}`,
            displayName: "Feature requests"
          }
        ]
      },
      {
        displayName: "Settings",
        icon: faGear,
        children: [
          { routerName: "iframe-user-connectwise-configuration", displayName: "ConnectWise", routerParams: { tenant_id: String(userStore.currentTenant?.id) } },
          { routerName: "AccountingConfig", displayName: "QuickBooks Online" },
          { routerName: "ManageMobiusUsers", displayName: "Manage Mobius users" },
          currentPlanIsNot(["qbo"]) && { routerName: "GozyntaPayments", displayName: "Gozynta Payments" },
          currentPlanIsNot(["qbo"]) && { routerName: "CustomerPortal", displayName: "Customer Portal", beta: true },
        ].filter(Boolean) as NavLink["children"]
      },
    ]
  },
]);

const adminNavSections = ref<NavMenuSection[]>([
  {
    links: [
      {
        routerName: "ManageTenants",
        displayName: "Manage tenants",
        icon: faBuildings
      },
      {
        routerName: "ManageAdminUsers",
        displayName: "Manage admin users",
        icon: faAddressCard
      },
      {
        displayName: "Latest activity",
        icon: faListCheck,
        children: [
          { routerName: "iframe-admin-tasks", displayName: "Recent tasks" },
          { routerName: "iframe-admin-scheduled-jobs", displayName: "Scheduled jobs" },
          { routerName: "iframe-admin-scheduled-modules", displayName: "Scheduled modules" },
        ]
      },
    ]
  },
]);

const navSections = computed<NavMenuSection[]>(() => {
  return showAdminMenu.value ? adminNavSections.value : userNavSections.value;
});

function hasChildren(link: NavLink) {
  return link.children && link.children.length > 0;
};
</script>

<template>
  <div :class="['menu-container', { open: props.isSidebarOpen }]">
    <aside class="sidebar">
      <nav class="sidebar-menu">
        <div
          v-for="(section, sectionIndex) in navSections"
          :key="section.category || sectionIndex"
          class="section"
        >
          <div v-if="section.category" class="section-title">
            {{ section.category }}
          </div>

          <div
            v-for="link in section.links"
            :key="link.displayName"
            :class="['item', { 'has-submenu': hasChildren(link) }]"
          >
            <template v-if="hasChildren(link)">
              <LinkWithSubmenu :link="link" />
            </template>

            <RouterLink
              v-else-if="link.routerName && (!link.routerParams || Object.values(link.routerParams).every(val => !!val))"
              :to="{
                name: link.routerName,
                params: link.routerParams ? link.routerParams : {}
              }"
              :class="['sidebar-menu-link', { active: route.name === link.routerName }]"
            >
              <span class="icon">
                <FontAwesomeIcon v-if="link.icon" :icon="link.icon" />
              </span>
              {{ link.displayName }}
              <StatusBadge v-if="link.beta" text="Beta" color="teal" />
            </RouterLink>

            <a
              v-else-if="link.externalUrl"
              :href="link.externalUrl"
              target="_blank"
              class="sidebar-menu-link"
            >
              <span class="icon">
                <FontAwesomeIcon v-if="link.icon" :icon="link.icon" />
              </span>
              {{ link.displayName }}
            </a>
          </div>

          <hr v-if="section.dividerAfter" class="section-divider">
        </div>
      </nav>
    </aside>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  $sidebar-width-mob: 280px;

  width: 300px;
  min-width: fit-content;
  flex: 0 1 300px;
  background-color: white;
  overflow: auto;
  @media (max-width: map.get(v.$breakpoints, "md")) {
    flex: unset;
    width: 100%;
    // 2px vertical overflow to avoid visual artefacts
    height: calc(100% + 4px);
    margin: -2px 0;
    padding: 2px 0;
    position: absolute;
    left: -280px;
    width: 280px;
    transition: left .2s;
    z-index: 4;
  }
  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 50px;
    position: sticky;
    top: 0;
    @media (max-width: map.get(v.$breakpoints, "md")) {
      gap: 30px;
      position: unset;
      z-index: 4;
    }
    &-menu {
      padding: 20px 10px;
      min-width: fit-content;
      .section {
        .section-title {
          padding: 20px 20px 10px;
          font-size: .75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: .05em;
          color: map.get(v.$gray, "400");
        }
        .section-divider {
          border: none;
          border-top: 1px solid map.get(v.$gray, "300");
          margin: 15px 20px 10px;
        }
        .item {
          .sidebar-menu-link, a.sidebar-menu-link {
            @include g.sidebarLinkStyles;
          }
        }
      }
    }
  }
  &.open {
    @media (max-width: map.get(v.$breakpoints, "md")) {
      left: 0;
    }
  }
}
</style>
